import { AnalyticsService } from './../analytics.service';
import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { disconnect, DisconnectReturnType, fallback, getAccount, http, reconnect } from "@wagmi/core";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";

import { environment } from "../../../environments/environment";
import { AppKit, ConnectedWalletInfo, UseAppKitAccountReturn } from "@reown/appkit";
import { AppKitNetwork, ChainNamespace } from "@reown/appkit/networks";
import { IReownConnectionItems } from "../../shared/interfaces/wallet.interface";
import { CHAINS } from "../../shared/constants/app-enums";
import { SolanaAdapter } from "@reown/appkit-adapter-solana";

type ReownAppKitModule = typeof import("@reown/appkit");
type SolanaAdapterModule = typeof import("@reown/appkit-adapter-solana");
type WagmiAdapterModule = typeof import("@reown/appkit-adapter-wagmi")

// requires only in this file
const APP_CONFIG = {
    name: environment.presaleName,
    description: 'Pegged to the MSCI Emerging Markets Index and backed by $459B+ real-world assets. Audited by TNF and verified by Chainlink PoR.',
    url: environment.domain, // origin must match your domain & subdomain
    icons: [environment.domain + '/assets/images/pngs/logo.png'],
}

// requires only in this file
export const FEATURED_WALLET_IDS = [
    'fe68cea63541aa53ce020de7398968566dfe8f3725663a564cac89490247ed49', // Best wallet
    'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // Phantom
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // Meta Mask
    "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0" // Trust wallet
];
@Injectable({
    providedIn: 'root',
})


export class ReownService {
    // it requires type to be imported from js module which will break our modular setup.
    reownKit!: ReownAppKitModule;
    $connectionItems = new BehaviorSubject<IReownConnectionItems>({} as IReownConnectionItems);
    $walletDisconnected = new BehaviorSubject<boolean>(false);
    wagmiAdapter!: WagmiAdapter;
    walletConnector!: AppKit;
    projectId: string = environment.reownProductId;
    connectedWalletName: string = "";
    evmWalletAddress: string = "";
    solWalletAddress: string = "";
    // it requires type to be imported from js module which will break our modular setup.
    solanaAdapter!: SolanaAdapter;
    sendVwoData = false;
    modalOpenedFromConnectWallet = false;

    constructor(
        @Inject(PLATFORM_ID) private _platformId: Object,
        private analyticsService: AnalyticsService
    ) {
        if (isPlatformBrowser(this._platformId)) {
            import("@reown/appkit").then((reownkit) => {
                this.reownKit = reownkit;
                import("@reown/appkit-adapter-wagmi").then((wagmiAdapter) => {
                    import("@reown/appkit-adapter-solana").then((solAdapter) => {
                        this.initialzeWalletAdapter(solAdapter, wagmiAdapter);
                    })
                })
            })
        }
    }


    async initialzeWalletAdapter(solAdapter: SolanaAdapterModule, wagmiAdapter: WagmiAdapterModule) {
        try {
            this.solanaAdapter = new solAdapter.SolanaAdapter();
            const { mainnet, solana, sepolia, solanaDevnet } = await import("@reown/appkit/networks");
            const allNetworksEVM = environment.production ? [mainnet] : [sepolia];
            const SOL = environment.production ? [solana] : [solanaDevnet];
            this.wagmiAdapter = new wagmiAdapter.WagmiAdapter({
                projectId: this.projectId,
                networks: allNetworksEVM,
                transports: {
                    [environment.eth.chainIdInt]: environment.production ? fallback([http(environment.eth.rpcURL), http(environment.eth.backupRPC)]) : http('https://1rpc.io/sepolia')
                },
            })
            this.$connectionItems.next({ wagmiConfig: this.wagmiAdapter.wagmiConfig })
            this.createAppKitInstance([...SOL, ...allNetworksEVM] as AppKitNetwork[]);

        } catch (error) {
            console.log({ error }, "---error in initilization")
        }
    }

    createAppKitInstance(networks: AppKitNetwork[]) {

        this.walletConnector = this.reownKit.createAppKit({
            adapters: [this.solanaAdapter, this.wagmiAdapter],
            networks: [...networks] as [AppKitNetwork, ...AppKitNetwork[]],
            metadata: APP_CONFIG,
            projectId: this.projectId,
            themeMode: 'dark',
            enableNetworkSwitch: true,
            enableWalletConnect: true,
            enableCoinbase: false,
            featuredWalletIds: [...FEATURED_WALLET_IDS.filter(item => !this.getExcludedIds().includes(item))],
            features: {
                email: false,
                socials: false,
                analytics: false,
                history: false,
                allWallets: true,
            },
        });
        this.subscribeEvents();
    }

    subscribeEvents() {

        this.walletConnector.subscribeWalletInfo((item: ConnectedWalletInfo | undefined) => {
            this.connectedWalletName = item?.name as string;
        })

        this.walletConnector.subscribeAccount(async (item: UseAppKitAccountReturn) => {
            if (!item.isConnected && item.status === "disconnected" && !this.modalOpenedFromConnectWallet) {
                this.walletConnector.close();
                this.removeAppKitReferenceFromLocalStorage();
                this.clearIndexedDB();
            }
            if (item.isConnected) {
                const activeChain = item?.caipAddress?.includes("solana") ? CHAINS.SOL : CHAINS.EVM
                const _wallets = await this.getActiveSessions();
                const solAddress = _wallets['solana'] || ""
                const evmAddress = _wallets['eip155'] || ""
                if (evmAddress && this.wagmiAdapter?.wagmiConfig?.state?.status !== "connected") {
                    await reconnect(this.wagmiAdapter?.wagmiConfig);
                }
                this.solWalletAddress = solAddress || '';
                this.evmWalletAddress = evmAddress || '';
                this.$connectionItems.next({ wagmiConfig: this.wagmiAdapter?.wagmiConfig, walletAddressEVM: evmAddress, walletAddressSOL: this.solWalletAddress, appKitInstance: this.walletConnector, activeChain: activeChain });
            }
        })

    }

    /**
         * Connects Selected EVM wallet
         * @param mode - Modes are the wallet type best wallet, wallet connect, coin base etc.
         * @param isBsNetwork - If this is BSC network
         */
    async connectWallet(chain: "SOL" | "ETH" | "" = "") {
        try {
            this.sendVwoData = true;
            this.analyticsService.vwoData('walletTypeSelect', { walletType: chain || "EVM/SOL" });
            const namespace = chain === 'ETH' ? 'eip155' : 'solana'
            // Metamask sol is not registered with Reown yet. So to keep multiple chain active is not possible for MM mobile app.
            if ((this.connectedWalletName?.toLowerCase() === 'metamask wallet')) {
                await this.disconnectWallet()
            }
            const isMobile = window.innerWidth <= 1024 ? true : false;
            this.modalOpenedFromConnectWallet = true;
            if (!chain) {
                if (isMobile) {
                    this.walletConnector.open({ view: 'Connect', namespace: 'eip155' });
                    return;
                }
                this.walletConnector.open({ view: 'Connect' })
            };
            if (chain) this.walletConnector.open({ view: 'Connect', namespace: namespace });
        } catch (e) {
            console.log(e, "---connector error");
            this.analyticsService.vwoData('walletConnectionFailed', { walletType: chain });
        }
    }

    /**
 * Disconnects wallet SOL/EVM
 * @returns void
 */
    async disconnectWallet(): Promise<void> {
        try {
            this.analyticsService.vwoData('disconnectWallet', { walletAddress: this.evmWalletAddress || this.solWalletAddress });
            await this.walletConnector.disconnect();
            this.walletConnector.close();
            this.removeAppKitReferenceFromLocalStorage();
            this.clearIndexedDB();
            await this.disconnectEvmInstances();
            this.evmWalletAddress = "";
            this.$walletDisconnected.next(true);
            this.$connectionItems.next({ wagmiConfig: this.wagmiAdapter?.wagmiConfig, walletAddressEVM: "", walletAddressSOL: "", appKitInstance: this.walletConnector, activeChain: CHAINS.EVM })

        } catch (error) {
            console.log("error in disconnect");

        }
    }

    async disconnectEvmInstances() {
        await disconnect(this.wagmiAdapter.wagmiConfig).then(
            async (success: DisconnectReturnType) => {
                const account = getAccount(this.wagmiAdapter.wagmiConfig);
                console.log('\n==== wallet disconnected ====\n', account);
                // checking all unwanted accounts connected using wallet injections;
                if (account.address) {
                    this.disconnectEvmInstances(); // It's temporary solution to disconnect wallet and will figureout the proper solution later.
                    return;
                }
                await this.wagmiAdapter.disconnect({ id: this.wagmiAdapter.getConnectorId('eip155') })
            },
            (err: any) => {
                console.log('\n==== wallet disconnect issue ====\n');
                console.log('disconnect issue =', err);
            },
        );
    }

    removeAppKitReferenceFromLocalStorage() {
        if (!isPlatformBrowser(this._platformId)) return;
        const keysToRemove: string[] = [];

        // Iterate over all keys in localStorage
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            // Check if the key exists and starts with the specified prefix
            if (key && (key.startsWith('@appkit') || key.startsWith('wagmi'))) {
                keysToRemove.push(key);
            }
        }
        // Remove the collected keys
        keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
        });
    }

    async getActiveSessions() {
        const activeChainNameSpaces = await this.walletConnector?.chainNamespaces;
        const addresses: { [key: string]: string } = {};
        if (activeChainNameSpaces.length) {
            activeChainNameSpaces.forEach(async (item: ChainNamespace) => {
                this.walletConnector.getAddress()
                const _address = this.walletConnector?.getAddress(item);
                if (_address) addresses[item] = _address
            })
        }
        return addresses;
    }

    async resetWcConnection() {
        this.walletConnector.resetWcConnection();
    }

    async clearIndexedDB() {
        // remove all the reown instance of indexedDB. 
        const databases = await window.indexedDB.databases();
        for (const db of databases) {
            if (db.name) {
                console.log('Deleting IndexedDB:', db.name);
                await new Promise<void>((resolve, reject) => {
                    const request = window.indexedDB.deleteDatabase(db.name!);
                    request.onsuccess = () => resolve();
                    request.onerror = () => reject(request.error);
                    request.onblocked = () => resolve(); // sometimes blocked by open tabs
                });
            }
            console.log('✅ All IndexedDB databases removed');
        }
    }

    getExcludedIds(): string[] {
        const excluded_ids = []
        //@ts-ignore
        const isTrustWallet = window.ethereum?.providers?.find((item: { isTrust: boolean }) => item.isTrust);
        //@ts-ignore
        const isMetamask = window.ethereum?.providers?.find((item: { isMetamask: boolean }) => item.isMetamask);
        //@ts-ignore
        const isPhantom = window.phantom?.solana?.isPhantom || window.phantom?.ethereum?.isPhantom;
        if (isMetamask) {
            excluded_ids.push("c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96")
        }
        if (isTrustWallet) {
            excluded_ids.push("4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0");
        }
        if (isPhantom) {
            excluded_ids.push("a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393")
        }
        return excluded_ids
    }
}
